import {Fragment, useMemo, useState} from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography
} from "@mui/material";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {MaterialReactTable} from "material-react-table";
import {Delete, Download, Edit} from "@mui/icons-material";
import {createIndustry, deleteIndustry, getAllIndustries, updateIndustry} from "../../api/industries";
import {download, generateCsv, mkConfig} from "export-to-csv";
import {industriesQueryKey} from "../../constants";


const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
});

const Industries = () => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState({name: "", id: ""});
    const queryClient = useQueryClient();

    const handleExport = (data) => {
        const csv = generateCsv(csvConfig)(data);
        download(csvConfig)(csv);
    }

    const openDeleteConfirmModal = (item) => {
        setItemToDelete(item.original);
        setDeleteDialogOpen(true);
    }

    const {
        isLoading: industriesLoading,
        data: industries = []
    } = useQuery(industriesQueryKey, getAllIndustries, {refetchOnWindowFocus: false});

    const {
        mutate: mutateCreateIndustry,
        isLoading: isCreateIndustryLoading
    } = useMutation({
        mutationFn: createIndustry,
        onSettled: () => queryClient.invalidateQueries(industriesQueryKey)
    })

    const {
        mutate: mutateDeleteIndustry,
        isLoading: isDeleteIndustryLoading
    } = useMutation({
        mutationFn: deleteIndustry,
        onSettled: () => queryClient.invalidateQueries(industriesQueryKey)
    });

    const {
        mutate: mutateUpdateIndustry,
        isLoading: isUpdateIndustryLoading
    } = useMutation({
        mutationFn: updateIndustry,
        onSettled: () => queryClient.invalidateQueries(industriesQueryKey)
    });

    const onCreateIndustry = async ({values, table}) => {
        await mutateCreateIndustry(values);
        table.setCreatingRow(false);
    }

    const onUpdateIndustry = async ({values, table}) => {
        await mutateUpdateIndustry(values);
        table.setEditingRow(false);
    }

    const onDeleteIndustry = async (industryId) => {
        await mutateDeleteIndustry(industryId);
        setDeleteDialogOpen(false);
    }

    const columns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'ID',
            type: 'number',
            enableEditing: false
        },
        {
            accessorKey: 'name',
            header: 'Name',
            type: 'string',
            enableEditing: true,

        }
    ], []);

    return (
        <Fragment>
            <Dialog open={deleteDialogOpen}>
                <DialogTitle>Delete Item</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete item "{itemToDelete.name}"?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => onDeleteIndustry(itemToDelete.id)}>Yes</Button>
                    <Button variant="outlined" onClick={() => setDeleteDialogOpen(false)}>No</Button>
                </DialogActions>
            </Dialog>
            <Typography variant="h4" sx={{mb: 5}}>Product Categories</Typography>
            <MaterialReactTable
                columns={columns}
                data={industries}
                enableEditing={true}
                state={{
                    isLoading: industriesLoading,
                    isSaving: isCreateIndustryLoading || isDeleteIndustryLoading || isUpdateIndustryLoading
                }}
                editDisplayMode={"row"} createDisplayMode={"row"}
                enablePagination={false}
                enableFullScreenToggle={false}
                enableDensityToggle={false}
                enableColumnFilterModes={true}
                initialState={{
                    showColumnFilters: true,
                }}
                renderRowActions={({row, table}) => {
                    return (
                        <Box sx={{display: 'flex', gap: '1rem'}}>
                            <Tooltip title="Edit">
                                <IconButton onClick={() => table.setEditingRow(row)}>
                                    <Edit/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
                                    <Delete/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                }}
                renderTopToolbarCustomActions={({table}) => {
                    return (
                        <Box display={"flex"} justifyContent={"flex-start"}>
                            <Button
                                variant="contained"
                                onClick={_ => table.setCreatingRow(true)}
                            >
                                Add Industry
                            </Button>
                            <Button onClick={_ => handleExport(industries)} variant={"contained"} sx={{ml: 2}}>
                                <Download/>
                                Download CSV
                            </Button>
                        </Box>

                    );

                }}
                onCreatingRowSave={onCreateIndustry}
                onEditingRowSave={onUpdateIndustry}
            />
        </Fragment>
    );
}

export default Industries;